import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { AssetSelect, CompanySelect, CreatePage, FuseInput, LoanSelect } from '/src/components';
import { useAdjustmentStore, useFilterStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { DataModelType, FormErrors } from '/src/lib/types';
import { CreateAdjustmentForm } from '/src/lib/types/adjustment-types';
import { currency, isNumber } from '/src/utils';

/**
 * Create Adjustment Page
 */
export const CreateAdjustmentPage: React.FC = observer(() => {
  /** Context **/
  const adjustmentStore = useAdjustmentStore();
  const filterStore = useFilterStore();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<CreateAdjustmentForm>({
    effectiveDate: '',
    name: '',
    amount: '',
    notes: [],
    files: [],
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Computed **/
  const projectedAssetValue =
    formValues.asset && formValues.amount
      ? formValues.asset.value + parseFloat(formValues.amount ?? '0')
      : formValues.asset?.value ?? 0;

  const companies = useMemo(() => filterStore.companyListGet(), [filterStore._companyList]);
  const loans = useMemo(() => filterStore.loanListGet(), [filterStore._loanList]);

  const filteredLoans = useMemo(() => {
    return formValues.company?._id
      ? loans.filter((loan) => loan.assets.length && loan.borrower._id === formValues.company?._id)
      : [];
  }, [loans, formValues.company]);

  const filteredAssets = formValues.loan ? formValues.loan.assets.toSorted((a, b) => a.name.localeCompare(b.name)) : [];

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    setFormValues((prev) => {
      const updates: Partial<CreateAdjustmentForm> = { [key]: value };

      // Clear dependent fields
      if (key === 'company') {
        updates.loan = undefined;
        updates.asset = undefined;
        updates.amount = undefined;
      } else if (key === 'loan') {
        updates.asset = undefined;
        updates.amount = undefined;
      } else if (key === 'asset') {
        // Reset amount when asset changes to avoid invalid calculations
        updates.amount = undefined;
      }

      return { ...prev, ...updates };
    });

    // Clear error when field is updated
    if (formErrors[key]) {
      setFormErrors((prev) => ({
        ...prev,
        [key]: '',
      }));
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formValues.company) errors.company = 'Company is required';
    if (!formValues.loan) errors.loan = 'Loan is required';
    if (!formValues.asset) errors.asset = 'Asset is required';
    if (!formValues.name) errors.name = 'Name is required';
    if (!isNumber(formValues.amount)) errors.amount = 'Valid amount is required';
    if (!formValues.effectiveDate) errors.effectiveDate = 'Date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Creates an Adjustment.
   *
   * @remarks
   * Redirects to the new Adjustment's Detail page afterwards.
   */
  const create = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors first.');
      return;
    }

    try {
      const response = await adjustmentStore.create(formValues);
      if (!response) throw new Error(`Could not create Adjustment with data: ${JSON.stringify(formValues)}`);

      toast.success(toastMessages.createAdjustment.success);
      navigate(`/adjustments/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.createAdjustment.error);
    }
  };

  /** Render **/
  return (
    <CreatePage modelType={DataModelType.Adjustment} onCreate={create}>
      <CompanySelect
        value={formValues.company?._id ?? ''}
        setValue={(companyId) => updateFormValue('company')(companies.find((company) => company._id === companyId))}
        companies={companies.filter((company) => company.loans?.length)}
        error={formErrors.company}
      />

      <LoanSelect
        value={formValues.loan?._id ?? ''}
        setValue={(loanId) => updateFormValue('loan')(filteredLoans.find((loan) => loan._id === loanId))}
        loans={filteredLoans}
        error={formErrors.loan}
      />

      <AssetSelect
        value={formValues.asset?._id ?? ''}
        setValue={(assetId) => updateFormValue('asset')(filteredAssets.find((asset) => asset._id === assetId))}
        assets={filteredAssets}
        error={formErrors.asset}
      />

      <FuseInput
        id="amount"
        label="Adjustment Amount"
        value={formValues.amount}
        updateValue={updateFormValue('amount')}
        type="number"
        moneyMask
        negativeNumber
        required
        error={formErrors.amount}
      />

      {formValues.asset && (
        <Box display="flex">
          <Box
            flex={50}
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            fontSize="1.2em"
            height="100%"
          >
            <Box paddingBottom="6px" marginBottom="6px" borderBottom="solid 1px rgba(0,0,0,0.12)" className="thin">
              Current Asset Value
            </Box>
            <Box id="value">{currency.format(formValues.asset.value)}</Box>
          </Box>

          <Box
            flex={50}
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            fontSize="1.2em"
            height="100%"
          >
            <Box paddingBottom="6px" marginBottom="6px" borderBottom="solid 1px rgba(0,0,0,0.12)" className="thin">
              Asset Value After Adjustment
            </Box>
            <Box id="projectedValue">{currency.format(projectedAssetValue)}</Box>
          </Box>
        </Box>
      )}

      <FuseInput
        label="Adjustment Name"
        value={formValues.name}
        updateValue={updateFormValue('name')}
        required
        error={formErrors.name}
      />

      <FuseInput
        label="Adjustment Date"
        value={formValues.effectiveDate}
        updateValue={updateFormValue('effectiveDate')}
        type="date"
        required
        error={formErrors.effectiveDate}
      />
    </CreatePage>
  );
});
